import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { ROUTES } from "../../../../constants/routes";
import { Box, CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  DownloadCircleIcon,
  ThreeDotsMenuIcon,
  RenameIcon,
  DeleteIcon,
} from "../../../design-system/icons";
import { Button } from "../../../design-system";
import { colors } from "../../../design-system/colors";
import cherryLogo from "../../../../assets/brand/logo_site.svg";
import {
  ACTION_RENAME,
  ACTION_DELETE,
  ACTION_START_EDITING,
  ACTION_AUTO_CLIP,
  ACTION_TOP_CLIPS,
  ACTION_TOP_CLIPS_TITLE,
  ACTION_DOWNLOAD,
  ACTION_RUN_WHISPER,
  ACTION_REGENERATE_THUMBNAILS,
  ACTION_REGENERATE_THUMBNAILS_QUEUING,
} from "../constants/myLibraryText";

interface RowActionsProps {
  videoId: number;
  videoTitle: string;
  videoUrl: string;
  onDownload: (videoId: number, videoUrl: string) => void;
  onDelete: (videoId: number, videoTitle: string) => void;
  onRename: (videoId: number, currentTitle: string) => void;
  /** True while a download is in progress for this row — disables the button. */
  isDownloading?: boolean;
  /** Optional: for Uploads tab - same action as clicking the row (e.g. navigate to create-video) */
  onStartEditing?: (videoId: number, videoTitle: string) => void;
  /** Show download as an inline icon button instead of inside the three-dots menu */
  downloadInline?: boolean;
  /** When provided, replaces the play/inline actions with a Copy to Clipboard button */
  onCopyToClipboard?: (videoUrl: string) => void;
  /** When provided, shows a "Run Whisper" item in the menu (only for AssemblyAI transcripts) */
  onRunWhisper?: (videoId: number) => void;
  /** True while Whisper is being queued for this row */
  isRunningWhisper?: boolean;
  /** When provided, shows a one-click 🍒 Auto-Clip button (highlights supercut export) */
  onAutoClip?: (videoId: number) => void;
  /** Uploads only: show the ▤ link to the Reel page. Never set from the Exports tab —
   *  `videoId` there is a creation id, not a transcript id. */
  showReel?: boolean;
  /** When provided, shows Auto-Clip as an item in the ⋮ menu (experimental, so not on the row) */
  onAutoClipMenu?: (videoId: number) => void;
  /** True while an auto-clip is being queued for this row */
  isAutoClipping?: boolean;
  /** When provided, shows a "Share link" menu item (creates a shareable ET link) */
  onShare?: (videoId: number) => void;
  /** When provided, shows a "Regenerate thumbnails" item (re-runs poster generation) */
  onRegenerateThumbnails?: (videoId: number) => void;
  /** True while thumbnail regeneration is being queued for this row */
  isRegeneratingThumbnails?: boolean;
}

/**
 * Row Actions Component
 * Displays action buttons (download, menu) visible on row hover
 */
const RowActions: React.FC<RowActionsProps> = ({
  videoId,
  videoTitle,
  videoUrl,
  onDownload,
  onDelete,
  onRename,
  isDownloading = false,
  onStartEditing,
  downloadInline = false,
  onCopyToClipboard,
  onRunWhisper,
  isRunningWhisper = false,
  onAutoClip,
  onAutoClipMenu,
  showReel,
  isAutoClipping = false,
  onShare,
  onRegenerateThumbnails,
  isRegeneratingThumbnails = false,
}): JSX.Element => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation(); // Prevent row click
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchor(null);
  };

  const handleDownloadClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onDownload(videoId, videoUrl);
    handleMenuClose();
  };

  const handleDeleteClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onDelete(videoId, videoTitle);
    handleMenuClose();
  };

  const handleRenameClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onRename(videoId, videoTitle);
    handleMenuClose();
  };

  const handleShareClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onShare?.(videoId);
    handleMenuClose();
  };

  const handleStartEditingClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onStartEditing?.(videoId, videoTitle);
    handleMenuClose();
  };

  const handleRunWhisperClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onRunWhisper?.(videoId);
    handleMenuClose();
  };

  const handleCopyToClipboardClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onCopyToClipboard?.(videoUrl);
  };

  const handleAutoClipClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onAutoClip?.(videoId);
  };

  const handleOpenReelClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    navigate(`${ROUTES.REEL}?t=${videoId}`);
  };

  const handleRegenerateThumbnailsClick = (event: React.MouseEvent): void => {
    event.stopPropagation(); // Prevent row click
    onRegenerateThumbnails?.(videoId);
    handleMenuClose();
  };

  return (
    <Box
      className={`row-actions${menuOpen ? " row-actions--menu-open" : ""}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        justifyContent: "flex-end",
      }}
    >
      {onAutoClip && (
        <IconButton
          size="small"
          onClick={handleAutoClipClick}
          disabled={isAutoClipping}
          title="Cherry-pick for me: stitch this video's highlights into a ≤30s supercut and send it to Exported (no AI cost)"
          aria-label="Auto cherry-pick highlights supercut"
          sx={{
            width: "34px",
            height: "34px",
            padding: "3px",
            "&:hover": { opacity: 0.8 },
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {isAutoClipping ? (
            <CircularProgress size={20} sx={{ color: colors.primary[500] }} />
          ) : (
            <img
              src={cherryLogo}
              alt=""
              style={{ height: "100%", width: "auto", display: "block" }}
            />
          )}
        </IconButton>
      )}

      {showReel && (
        <IconButton
          size="small"
          onClick={handleOpenReelClick}
          title={ACTION_TOP_CLIPS_TITLE}
          aria-label={ACTION_TOP_CLIPS}
          sx={{
            width: "34px",
            height: "34px",
            fontSize: "18px",
            color: colors.neutral.white,
            "&:hover": { opacity: 0.8 },
          }}
        >
          ▤
        </IconButton>
      )}

      {onStartEditing && (
        <Button
          variant="primary"
          onClick={handleStartEditingClick}
          aria-label="Start editing"
          sx={{ height: "32px", fontSize: "14px", whiteSpace: "nowrap" }}
        >
          {ACTION_START_EDITING}
        </Button>
      )}

      {onCopyToClipboard && (
        <IconButton
          size="small"
          onClick={handleCopyToClipboardClick}
          sx={{ padding: 0, color: colors.neutral.white, "&:hover": { opacity: 0.9 } }}
          aria-label="Copy to clipboard"
        >
          <ContentCopyIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      )}

      {downloadInline && !onCopyToClipboard && (
        <IconButton
          size="small"
          onClick={handleDownloadClick}
          disabled={isDownloading}
          sx={{ padding: 0, "&:hover": { opacity: 0.9 } }}
          aria-label="Download video"
        >
          {isDownloading ? (
            <CircularProgress size={18} sx={{ color: colors.neutral.white }} />
          ) : (
            <DownloadCircleIcon size={30} />
          )}
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={handleMenuClick}
        sx={{
          width: "32px",
          height: "32px",
          padding: 0,
          borderRadius: "50%",
          "&:hover": { opacity: 0.9 },
        }}
        aria-label="More actions"
      >
        <ThreeDotsMenuIcon
          size={32}
          bgColor={menuOpen ? colors.primary[500] : colors.background[600]}
        />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            backgroundColor: colors.background.menuDropdown,
            color: colors.neutral.white,
            minWidth: "120px",
            borderRadius: "8px",
            marginTop: "8px",
          },
        }}
      >
        {!downloadInline && (
          <MenuItem onClick={handleDownloadClick} disabled={isDownloading}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {isDownloading ? (
                <CircularProgress size={18} sx={{ color: colors.neutral.white }} />
              ) : (
                <DownloadCircleIcon size={26} bgColor="transparent" />
              )}
              {ACTION_DOWNLOAD}
            </Box>
          </MenuItem>
        )}
        <MenuItem onClick={handleRenameClick}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <RenameIcon size={20} />
            {ACTION_RENAME}
          </Box>
        </MenuItem>
        {onShare && (
          <MenuItem onClick={handleShareClick}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>🔗</span>
              Share link
            </Box>
          </MenuItem>
        )}
        {onAutoClipMenu && (
          <MenuItem
            onClick={(e) => { e.stopPropagation(); handleMenuClose(); onAutoClipMenu(videoId); }}
            disabled={isAutoClipping}
            title="A highlights supercut of this video, sent to Exported (no AI cost)"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>✨</span>
              {isAutoClipping ? "Queuing…" : ACTION_AUTO_CLIP}
            </Box>
          </MenuItem>
        )}
        {onRunWhisper && (
          <MenuItem onClick={handleRunWhisperClick} disabled={isRunningWhisper}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>🎙️</span>
              {isRunningWhisper ? "Queuing…" : ACTION_RUN_WHISPER}
            </Box>
          </MenuItem>
        )}
        {onRegenerateThumbnails && (
          <MenuItem onClick={handleRegenerateThumbnailsClick} disabled={isRegeneratingThumbnails}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>🖼️</span>
              {isRegeneratingThumbnails ? ACTION_REGENERATE_THUMBNAILS_QUEUING : ACTION_REGENERATE_THUMBNAILS}
            </Box>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DeleteIcon size={20} />
            {ACTION_DELETE}
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default RowActions;
